import { useState } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Plus, Trash2, Phone, User } from 'lucide-react';
import { toast } from 'sonner';

export default function ContactsTab({ userId, contacts, refreshData }) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    relationship: '',
    phone: '',
    priority: 1
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.phone) {
      toast.error('Name and phone are required');
      return;
    }

    try {
      const payload = {
        user_id: userId,
        name: formData.name,
        relationship: formData.relationship || 'Contact',
        phone: formData.phone,
        priority: parseInt(formData.priority),
        notification_channels: ['call']
      };

      await axios.post(`${API}/emergency-contacts`, payload);
      toast.success('Emergency contact added!');
      setIsDialogOpen(false);
      setFormData({
        name: '',
        relationship: '',
        phone: '',
        priority: contacts.length + 1
      });
      await refreshData();
    } catch (error) {
      console.error('Error adding contact:', error);
      toast.error('Failed to add contact');
    }
  };

  const handleDelete = async (contactId) => {
    try {
      await axios.delete(`${API}/emergency-contacts/${contactId}`);
      toast.success('Contact deleted');
      await refreshData();
    } catch (error) {
      toast.error('Failed to delete contact');
    }
  };

  return (
    <div data-testid="contacts-tab">
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-lg font-semibold">Emergency Contacts</h3>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-contact-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Contact
            </Button>
          </DialogTrigger>
          <DialogContent data-testid="add-contact-dialog">
            <DialogHeader>
              <DialogTitle>Add Emergency Contact</DialogTitle>
              <DialogDescription>
                Add a trusted person to call in case of emergency
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="name">Name *</Label>
                <Input
                  data-testid="contact-name-input"
                  id="name"
                  placeholder="e.g., Sarah Johnson"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>

              <div>
                <Label htmlFor="relationship">Relationship</Label>
                <Input
                  data-testid="contact-relationship-input"
                  id="relationship"
                  placeholder="e.g., Daughter, Son, Caregiver"
                  value={formData.relationship}
                  onChange={(e) => setFormData({ ...formData, relationship: e.target.value })}
                />
              </div>

              <div>
                <Label htmlFor="phone">Phone Number *</Label>
                <Input
                  data-testid="contact-phone-input"
                  id="phone"
                  type="tel"
                  placeholder="e.g., +1 234 567 8900"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  required
                />
              </div>

              <div>
                <Label htmlFor="priority">Priority (1 = First to call)</Label>
                <Input
                  data-testid="contact-priority-input"
                  id="priority"
                  type="number"
                  min="1"
                  value={formData.priority}
                  onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  Lower numbers are called first in emergencies
                </p>
              </div>

              <Button data-testid="submit-contact-btn" type="submit" className="w-full">
                Add Contact
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {contacts.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <Phone className="w-16 h-16 mx-auto mb-4 opacity-30" />
          <p>No emergency contacts yet. Add at least one for safety.</p>
        </div>
      ) : (
        <ScrollArea className="h-[500px]">
          <div className="space-y-3">
            {contacts.map((contact) => (
              <Card key={contact.id} data-testid={`contact-card-${contact.id}`}>
                <CardContent className="pt-4">
                  <div className="flex justify-between items-start">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center">
                        <User className="w-6 h-6 text-indigo-600" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h4 className="font-semibold text-gray-900">{contact.name}</h4>
                          <Badge variant="outline">Priority {contact.priority}</Badge>
                        </div>
                        <p className="text-sm text-gray-600 mt-1">{contact.relationship}</p>
                        <div className="flex items-center gap-2 mt-2">
                          <Phone className="w-4 h-4 text-gray-400" />
                          <span className="text-sm text-gray-700">{contact.phone}</span>
                        </div>
                      </div>
                    </div>
                    <Button
                      data-testid={`delete-contact-btn-${contact.id}`}
                      size="sm"
                      variant="destructive"
                      onClick={() => handleDelete(contact.id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </ScrollArea>
      )}
    </div>
  );
}